"use client"

import { useEffect, useRef } from "react"
import { Text, clx } from "@medusajs/ui"
import Button from "@/modules/common/components/button"
import {
  formatDistanceKm,
  formatGlsDeliveryPointLabel,
  getGlsDeliveryPointTypeLabel,
  getOpenStreetMapUrl,
  type GlsDeliveryPointWithDistance,
} from "@/lib/util/gls-shipping"

type GlsDeliveryPointListProps = {
  points: GlsDeliveryPointWithDistance[]
  selectedMatchcode?: string | null
  focusedMatchcode?: string | null
  saving?: boolean
  showDistance?: boolean
  onFocusPoint: (matchcode: string) => void
  onSelectPoint: (point: GlsDeliveryPointWithDistance) => void
}

const GlsDeliveryPointList = ({
  points,
  selectedMatchcode,
  focusedMatchcode,
  saving = false,
  showDistance = true,
  onFocusPoint,
  onSelectPoint,
}: GlsDeliveryPointListProps) => {
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  useEffect(() => {
    if (!focusedMatchcode) {
      return
    }

    const row = rowRefs.current.get(focusedMatchcode)
    row?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }, [focusedMatchcode])

  if (!points.length) {
    return null
  }

  return (
    <div className="max-h-72 overflow-y-auto flex flex-col gap-2">
      {points.map((point) => {
        const mapUrl = getOpenStreetMapUrl(point)
        const isSelected = selectedMatchcode === point.matchcode
        const isFocused = focusedMatchcode === point.matchcode
        const distanceLabel = showDistance
          ? formatDistanceKm(point.distanceKm)
          : null

        return (
          <div
            key={point.matchcode}
            ref={(element) => {
              if (element) {
                rowRefs.current.set(point.matchcode, element)
              } else {
                rowRefs.current.delete(point.matchcode)
              }
            }}
            className={clx(
              "rounded-md border p-3 bg-ui-bg-base cursor-pointer transition-colors",
              isSelected || isFocused
                ? "border-ui-border-interactive"
                : "border-ui-border-base hover:border-ui-border-strong"
            )}
            onClick={() => onFocusPoint(point.matchcode)}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Text className="txt-small-plus">{point.name}</Text>
                  {distanceLabel && (
                    <span className="txt-compact-small text-ui-fg-muted">
                      {distanceLabel}
                    </span>
                  )}
                </div>
                <Text className="txt-small text-ui-fg-subtle">
                  {[point.street, point.houseNumber].filter(Boolean).join(" ")}
                  , {point.zipCode} {point.city}
                </Text>
                <Text className="txt-compact-small text-ui-fg-muted mt-1">
                  {getGlsDeliveryPointTypeLabel(point.type)}
                </Text>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button
                  size="small"
                  variant={isSelected ? "primary" : "secondary"}
                  isLoading={saving && isSelected}
                  onClick={(event) => {
                    event.stopPropagation()
                    onSelectPoint(point)
                  }}
                >
                  {isSelected ? "Izbrano" : "Izberi"}
                </Button>
                {mapUrl && (
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="txt-compact-small text-ui-fg-interactive hover:underline text-center"
                    onClick={(event) => event.stopPropagation()}
                  >
                    Zemljevid
                  </a>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default GlsDeliveryPointList
